import React, { useEffect, useRef, useState } from "react";
import { api, formatEUR } from "@/lib/api";
import { toast } from "sonner";
import { Users as UsersIcon, Plus, Star, Search, X, Trophy, Crown, Cake, PawPrint, Download, Upload, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { downloadFile } from "@/lib/download";
import { useFeatures } from "@/lib/features";

const SKIP_LISTS = new Set(["lavanderia", "bnb"]);
const SALA_MODES = new Set(["ristorante", "bar", "pizzeria", "festa"]);
const MODE_LABELS = {
  toelettatura: "Toelettatura",
  negozio_animali: "Negozio",
  negozio: "Negozio",
  ristorante: "Ristorante",
  bar: "Bar",
  pizzeria: "Pizzeria",
  festa: "Feste",
  parrucchiere: "Parrucchiere",
  alimentari: "Alimentari",
  macelleria: "Macelleria",
  panetteria: "Panetteria",
  gelateria: "Gelateria",
  enoteca: "Enoteca",
  tabacchi: "Tabacchi",
  edicola: "Edicola",
  autolavaggio: "Autolavaggio",
};
const modeLabel = (m) => MODE_LABELS[m] || String(m || "").replace(/_/g, " ");
const customerKind = (m) => (m === "toelettatura" ? "pet" : SALA_MODES.has(m) ? "sala" : "retail");

export default function Loyalty() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [top, setTop] = useState({ rows: [], period_days: 30 });
  const [listMode, setListMode] = useState("");
  const fileRef = useRef(null);
  const { modes, ready } = useFeatures();
  const lists = (Array.isArray(modes) ? modes : []).filter((m) => m && !SKIP_LISTS.has(m));
  const kind = customerKind(listMode);
  const showPet = kind === "pet";

  useEffect(() => {
    if (!lists.length) return;
    if (!lists.includes(listMode)) setListMode(lists[0]);
  }, [modes, listMode]);

  const load = () => api.get("/customers", { params: { limit: 1000, mode: listMode } }).then(r => setCustomers(Array.isArray(r.data) ? r.data : []));
  const loadTop = (days = 30) => api.get("/reports/top-customers", { params: { days, limit: 5, mode: listMode } }).then(r => setTop(r.data || { rows: [] })).catch(() => {});
  useEffect(() => {
    if (!listMode) return;
    setSearch("");
    load();
    loadTop(30);
  }, [listMode]);

  const filtered = (Array.isArray(customers) ? customers : []).filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [c.name, c.owner_name, c.pet_name, c.pet_breed].some(v => (v || "").toLowerCase().includes(s)) || (c.phone || "").includes(search);
  });

  const save = async (data) => {
    try {
      const payload = { ...data, mode: listMode };
      if (kind !== "pet") {
        payload.pet_name = "";
        payload.pet_type = "";
        payload.pet_breed = "";
        payload.pet_birthdate = "";
        payload.pet_allergies = "";
        payload.pet_character = "";
        payload.birthday_discount_enabled = false;
      }
      if (kind === "sala") payload.owner_name = "";
      if (kind !== "sala") payload.allergies = "";
      if (data.id) await api.patch(`/customers/${data.id}`, payload);
      else await api.post("/customers", payload);
      toast.success("Cliente salvato");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const exportExcel = async () => {
    try { await downloadFile(`/customers/export-excel?mode=${encodeURIComponent(listMode)}`, "clienti_velta.xlsx"); toast.success("Export avviato"); }
    catch { toast.error("Errore export"); }
  };

  const downloadTemplate = async () => {
    try { await downloadFile("/customers/export-excel?template=1", "template_clienti_velta.xlsx"); toast.success("Template scaricato"); }
    catch { toast.error("Errore download"); }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/customers/import-excel?mode=${encodeURIComponent(listMode)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import completato: ${r.data.created} nuovi, ${r.data.updated} aggiornati`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore import"); }
    e.target.value = "";
  };

  const removeCustomer = async (c) => {
    const who = c.pet_name || c.name || "questo cliente";
    if (!window.confirm(`Eliminare ${who}? La scheda e i punti vengono cancellati. Gli scontrini già emessi restano.`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      toast.success("Cliente eliminato");
      load();
      loadTop();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const adjustPoints = async (cid, delta, reason) => {
    try {
      await api.post(`/customers/${cid}/loyalty`, { delta_points: delta, reason });
      toast.success(`${delta > 0 ? "+" : ""}${delta} punti`);
      setAdjustTarget(null); load(); loadTop();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const totalPoints = customers.reduce((s, c) => s + (c.loyalty_points || 0), 0);
  const colSpan = showPet ? 6 : 5;
  const blurb = kind === "pet"
    ? "Schede della toelettatura: proprietario, animale, razza, compleanno e punti."
    : kind === "sala"
      ? "Clienti di questa cassa: nome, telefono, allergie e note."
      : "Clienti di questa cassa. I punti restano solo qui.";

  if (!ready) return null;

  if (ready && lists.length === 0) {
    return (
      <div className="p-8 lg:p-12 max-w-3xl">
        <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Fedeltà</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Clienti</h1>
        <p className="text-velta-mute mt-4">
          {((modes || []).includes("bnb") || (modes || []).includes("alloggio_dedicato")) && (modes || []).includes("lavanderia")
            ? "I clienti dell'alloggio stanno in Alberghiero: nome, documento e telefono. Quelli della lavanderia stanno in cassa, con la data di ritiro."
            : ((modes || []).includes("bnb") || (modes || []).includes("alloggio_dedicato"))
              ? "I clienti dell'alloggio stanno nel menu Clienti: nome, documento e telefono. Non sono i clienti delle casse."
              : "Per la lavanderia i clienti stanno in cassa: nome, telefono e data di ritiro. Non sono i clienti punti della toelettatura o del ristorante."}
        </p>
      </div>
    );
  }

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Fedeltà</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight">Clienti & Punti</h1>
          <p className="text-velta-mute mt-2">{blurb}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} />
          <button data-testid="loy-import-btn" onClick={() => fileRef.current?.click()} className="h-12 px-4 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md font-medium inline-flex items-center gap-2 btn-tactile">
            <Upload className="w-4 h-4" /> Importa Excel
          </button>
          <button data-testid="loy-export-btn" onClick={exportExcel} className="h-12 px-4 bg-white border border-velta-border text-velta-ink hover:border-velta-ink rounded-md font-medium inline-flex items-center gap-2 btn-tactile">
            <Download className="w-4 h-4" /> Esporta Excel
          </button>
          <button data-testid="loy-template-btn" onClick={downloadTemplate} title="Scarica un Excel vuoto con le sole intestazioni, pronto da compilare" className="h-12 px-4 bg-white border border-dashed border-velta-ink text-velta-ink hover:bg-velta-bg rounded-md font-medium inline-flex items-center gap-2 btn-tactile">
            <Download className="w-4 h-4" /> Template vuoto
          </button>
          <button data-testid="loy-new-btn" onClick={() => setEditing({ birthday_discount_enabled: false, birthday_discount_pct: 10 })} className="h-12 px-5 bg-[#2F9E77] hover:bg-[#27855F] text-white rounded-md font-medium inline-flex items-center gap-2 btn-tactile">
            <Plus className="w-4 h-4" /> Nuovo cliente
          </button>
        </div>
      </div>

      {lists.length > 1 && (
        <div className="flex flex-wrap gap-2 mb-6" data-testid="loy-mode-tabs">
          {lists.map((m) => (
            <button
              key={m}
              data-testid={`loy-mode-${m}`}
              onClick={() => setListMode(m)}
              className={`h-10 px-4 rounded-md text-sm font-medium btn-tactile ${listMode === m ? "bg-[#1E2D5B] text-white" : "bg-white border border-velta-border text-velta-ink"}`}
            >
              {modeLabel(m)}
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Clienti totali</div>
          <div data-testid="loy-stat-customers" className="font-mono font-bold text-3xl tracking-tighter">{customers.length}</div>
        </div>
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Punti circolanti</div>
          <div data-testid="loy-stat-points" className="font-mono font-bold text-3xl tracking-tighter">{totalPoints}</div>
        </div>
        <div className="bg-white border border-velta-border rounded-md p-5">
          <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-1">Valore stimato sconti</div>
          <div className="font-mono font-bold text-3xl tracking-tighter">{formatEUR(totalPoints * 0.05)}</div>
          <div className="text-xs text-velta-mute mt-1">@ 100pt = 5€</div>
        </div>
      </div>

      {/* Top Clienti widget */}
      {top.rows.length > 0 && (
        <div data-testid="loy-top-customers" className="bg-gradient-to-br from-[#1E2D5B] to-[#22335E] text-white rounded-md p-6 mb-6 shadow-lg">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-300" />
              <h2 className="font-heading font-semibold text-lg">Top clienti — ultimi {top.period_days} giorni</h2>
            </div>
            <div className="flex gap-1">
              {[7, 30, 90].map(d => (
                <button
                  key={d}
                  data-testid={`loy-top-period-${d}`}
                  onClick={() => loadTop(d)}
                  className={`px-2.5 py-1 rounded text-xs font-mono ${top.period_days === d ? "bg-white text-[#1E2D5B] font-bold" : "bg-white/10 hover:bg-white/20"}`}
                >
                  {d}g
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {(Array.isArray(top.rows) ? top.rows : []).map((r, i) => (
              <div key={r.customer_id} data-testid={`loy-top-row-${i}`} className="flex items-center gap-3 bg-white/5 hover:bg-white/10 rounded-md p-3 transition">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${i === 0 ? "bg-amber-400 text-[#1E2D5B]" : i === 1 ? "bg-slate-300 text-[#1E2D5B]" : i === 2 ? "bg-amber-700 text-white" : "bg-white/10 text-white/70"}`}>
                  {i === 0 ? <Crown className="w-4 h-4" /> : i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{r.name}</div>
                  <div className="text-xs text-white/60">{r.period_visits} visit{r.period_visits === 1 ? "a" : "e"} · {r.phone || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono font-bold">{formatEUR(r.period_spent)}</div>
                  <div className="text-[11px] text-amber-300 inline-flex items-center gap-1 justify-end">
                    <Star className="w-3 h-3 fill-current" /> {r.loyalty_points} pt
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-velta-border rounded-md p-4 mb-4 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-3 text-velta-mute" />
          <input
            data-testid="loy-search"
            placeholder={showPet ? "Cerca per proprietario, animale o telefono…" : "Cerca per nome o telefono…"}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full h-10 pl-9 pr-3 border border-velta-border rounded-md text-sm"
          />
        </div>
      </div>

      <div className="bg-white border border-velta-border rounded-md overflow-x-auto pos-scroll">
        <table className="w-full text-sm min-w-[920px]">
          <thead className="bg-velta-bg border-b border-velta-border text-xs uppercase tracking-widest text-velta-mute">
            <tr>
              <th className="text-left px-4 py-3">Cliente</th>
              {showPet && <th className="text-left px-4 py-3">Animale</th>}
              <th className="text-left px-4 py-3">Telefono</th>
              {kind === "sala" ? <th className="text-left px-4 py-3">Allergie</th> : <th className="text-left px-4 py-3">Email</th>}
              <th className="text-right px-4 py-3">Punti</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={colSpan} className="text-center text-velta-mute py-12"><UsersIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />Nessun cliente in questa cassa.</td></tr>
            ) : (Array.isArray(filtered) ? filtered : []).map(c => (
              <tr key={c.id} className="border-t border-velta-border hover:bg-velta-bg">
                <td className="px-4 py-3 font-medium">
                  <span className="inline-flex items-center gap-2">
                    {c.name}
                    {showPet && c.pet_birthday_today && (
                      <span data-testid={`loy-bday-${c.id}`} className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold uppercase">
                        <Cake className="w-3 h-3" /> Compleanno
                      </span>
                    )}
                  </span>
                  {c.owner_name && <div className="text-[11px] text-velta-mute font-normal">Prop. {c.owner_name}</div>}
                </td>
                {showPet && (
                <td className="px-4 py-3 text-xs">
                  {c.pet_name ? (
                    <span className="inline-flex items-center gap-1.5">
                      <PawPrint className="w-3.5 h-3.5 text-velta-mute" />
                      <span className="font-medium">{c.pet_name}</span>
                      <span className="text-velta-mute">{[c.pet_type, c.pet_breed].filter(Boolean).join(" · ")}</span>
                    </span>
                  ) : <span className="text-velta-mute">—</span>}
                </td>
                )}
                <td className="px-4 py-3 text-velta-mute font-mono">{c.phone || "—"}</td>
                <td className="px-4 py-3 text-velta-mute text-xs">{kind === "sala" ? (c.allergies || "—") : (c.email || "—")}</td>
                <td className="px-4 py-3 text-right">
                  <span className="inline-flex items-center gap-1 bg-[#F59E0B]/10 text-[#F59E0B] px-2 py-1 rounded font-mono font-bold">
                    <Star className="w-3 h-3 fill-current" /> {c.loyalty_points || 0}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button data-testid={`loy-points-${c.id}`} onClick={() => setAdjustTarget(c)} className="text-xs font-medium text-[#2F9E77] hover:underline mr-3">Punti</button>
                  <button data-testid={`loy-edit-${c.id}`} onClick={() => setEditing(c)} className="text-xs font-medium text-velta-ink hover:underline mr-3">Modifica</button>
                  <button data-testid={`loy-del-${c.id}`} onClick={() => removeCustomer(c)} className="text-xs font-medium text-velta-error hover:underline inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Elimina</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && <CustomerModal customer={editing} showPet={showPet} kind={kind} onClose={() => setEditing(null)} onSave={save} />}

      {adjustTarget && (
        <PointsAdjustModal
          customer={adjustTarget}
          onClose={() => setAdjustTarget(null)}
          onAdjust={(delta, reason) => adjustPoints(adjustTarget.id, delta, reason)}
        />
      )}
    </div>
  );
}

function CustomerModal({ customer, showPet, kind = "retail", onClose, onSave }) {
  const [f, setF] = useState({ birthday_discount_pct: 10, ...customer });
  const [otherMode, setOtherMode] = useState(Boolean(customer.pet_type) && !["Cane", "Gatto"].includes(customer.pet_type));
  const typeChoice = otherMode ? "Altro" : (f.pet_type === "Cane" || f.pet_type === "Gatto" ? f.pet_type : "");
  const inputCls = "w-full h-11 px-3 border border-velta-border rounded-md outline-none focus:border-[#1E2D5B]";
  const labelCls = "block text-xs font-mono uppercase tracking-widest text-velta-mute mb-1.5";

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-lg max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center p-6 pb-4 border-b border-velta-border">
          <h3 className="font-heading text-xl font-semibold">{f.id ? "Modifica" : "Nuovo"} cliente</h3>
          <button data-testid="loy-form-close" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {kind === "sala" ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Nome</label>
                <input data-testid="loy-form-name" value={f.name || ""} onChange={e => setF({ ...f, name: e.target.value })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Telefono</label>
                <input data-testid="loy-form-phone" value={f.phone || ""} onChange={e => setF({ ...f, phone: e.target.value })} className={`${inputCls} font-mono`} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Allergie</label>
                <textarea data-testid="loy-form-allergies" rows={2} value={f.allergies || ""} onChange={e => setF({ ...f, allergies: e.target.value })} className="w-full px-3 py-2 border border-velta-border rounded-md text-sm outline-none focus:border-[#1E2D5B]" />
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>{showPet ? "Nome in cassa" : "Nome cliente"}</label>
              <input data-testid="loy-form-name" value={f.name || ""} onChange={e => setF({ ...f, name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>{showPet ? "Proprietario" : "Nome proprietario"}</label>
              <input data-testid="loy-form-owner" value={f.owner_name || ""} onChange={e => setF({ ...f, owner_name: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Telefono</label>
              <input data-testid="loy-form-phone" value={f.phone || ""} onChange={e => setF({ ...f, phone: e.target.value })} className={`${inputCls} font-mono`} />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <input data-testid="loy-form-email" type="email" value={f.email || ""} onChange={e => setF({ ...f, email: e.target.value })} className={inputCls} />
            </div>
          </div>
          )}

          {showPet && <div className="border border-velta-border rounded-md p-4 bg-velta-bg/50">
            <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><PawPrint className="w-4 h-4 text-[#1E2D5B]" /> Scheda animale</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nome animale</label>
                <input data-testid="loy-form-pet-name" value={f.pet_name || ""} onChange={e => setF({ ...f, pet_name: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Razza</label>
                <input data-testid="loy-form-pet-breed" value={f.pet_breed || ""} onChange={e => setF({ ...f, pet_breed: e.target.value })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Tipo</label>
                <div className="flex gap-2">
                  {["Cane", "Gatto", "Altro"].map(t => (
                    <button
                      key={t}
                      data-testid={`loy-form-pet-type-${t.toLowerCase()}`}
                      onClick={() => {
                        if (t === "Altro") {
                          setOtherMode(true);
                          if (f.pet_type === "Cane" || f.pet_type === "Gatto") setF({ ...f, pet_type: "" });
                        } else {
                          setOtherMode(false);
                          setF({ ...f, pet_type: t });
                        }
                      }}
                      className={`h-10 px-4 rounded-md text-sm font-medium btn-tactile ${typeChoice === t ? "bg-[#1E2D5B] text-white" : "bg-white border border-velta-border"}`}
                    >{t}</button>
                  ))}
                  {typeChoice === "Altro" && (
                    <input data-testid="loy-form-pet-type-other" placeholder="Specifica (es. Coniglio)" value={f.pet_type || ""} onChange={e => setF({ ...f, pet_type: e.target.value })} className={`${inputCls} flex-1 h-10`} />
                  )}
                </div>
              </div>
              <div>
                <label className={labelCls}>Data di nascita</label>
                <input data-testid="loy-form-pet-birthdate" type="date" value={f.pet_birthdate || ""} onChange={e => setF({ ...f, pet_birthdate: e.target.value })} className={`${inputCls} font-mono`} />
              </div>
              <div />
              <div>
                <label className={labelCls}>Note allergie</label>
                <textarea data-testid="loy-form-pet-allergies" rows={2} value={f.pet_allergies || ""} onChange={e => setF({ ...f, pet_allergies: e.target.value })} className="w-full px-3 py-2 border border-velta-border rounded-md text-sm outline-none focus:border-[#1E2D5B]" />
              </div>
              <div>
                <label className={labelCls}>Note carattere</label>
                <textarea data-testid="loy-form-pet-character" rows={2} value={f.pet_character || ""} onChange={e => setF({ ...f, pet_character: e.target.value })} className="w-full px-3 py-2 border border-velta-border rounded-md text-sm outline-none focus:border-[#1E2D5B]" />
              </div>
            </div>
          </div>}

          {showPet && <div className="border border-amber-300 bg-amber-50/60 rounded-md p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Cake className="w-4 h-4 text-amber-600" />
                <div>
                  <div className="text-sm font-semibold">Sconto compleanno</div>
                  <div className="text-[11px] text-velta-mute">Applicato automaticamente in cassa il giorno del compleanno dell&apos;animale.</div>
                </div>
              </div>
              <Switch data-testid="loy-form-bday-toggle" checked={!!f.birthday_discount_enabled} onCheckedChange={(v) => setF({ ...f, birthday_discount_enabled: v })} />
            </div>
            {f.birthday_discount_enabled && (
              <div className="mt-3 flex items-center gap-2">
                <label className="text-xs font-mono uppercase tracking-widest text-velta-mute">Sconto</label>
                <input data-testid="loy-form-bday-pct" type="number" min="0" max="100" step="0.5" value={f.birthday_discount_pct ?? 10} onChange={e => setF({ ...f, birthday_discount_pct: parseFloat(e.target.value) || 0 })} className="h-10 w-24 px-3 border border-velta-border rounded-md font-mono text-center outline-none focus:border-[#1E2D5B]" />
                <span className="font-mono font-bold">%</span>
              </div>
            )}
          </div>}

          <div>
            <label className={labelCls}>Note</label>
            <textarea data-testid="loy-form-notes" rows={2} value={f.notes || ""} onChange={e => setF({ ...f, notes: e.target.value })} className="w-full px-3 py-2 border border-velta-border rounded-md text-sm outline-none focus:border-[#1E2D5B]" />
          </div>
        </div>
        <div className="p-6 pt-4 border-t border-velta-border">
          <button data-testid="loy-form-save" onClick={() => onSave(f)} className="w-full h-12 bg-velta-ink text-white rounded-md font-semibold btn-tactile">Salva cliente</button>
        </div>
      </div>
    </div>
  );
}

function PointsAdjustModal({ customer, onClose, onAdjust }) {
  const [delta, setDelta] = useState(10);
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className="bg-white rounded-md w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">Aggiusta punti</div>
            <h3 className="font-heading text-xl font-semibold">{customer.name}</h3>
            <div className="text-xs text-velta-mute mt-1">Punti attuali: <span className="font-mono font-bold text-velta-ink">{customer.loyalty_points || 0}</span></div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-2">
            {[10, 50, 100].map(v => (
              <button key={v} data-testid={`loy-quick-add-${v}`} onClick={() => setDelta(v)} className={`h-12 rounded-md font-mono font-bold ${delta === v ? "bg-[#2F9E77] text-white" : "bg-velta-bg border border-velta-border"}`}>+{v}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="loy-delta-minus" onClick={() => setDelta(-Math.abs(delta))} className="h-11 px-3 border border-velta-border rounded-md">−</button>
            <input
              data-testid="loy-delta-input"
              type="number"
              value={delta}
              onChange={e => setDelta(parseInt(e.target.value) || 0)}
              className="flex-1 h-11 px-3 border border-velta-border rounded-md font-mono font-bold text-2xl text-center"
            />
            <button data-testid="loy-delta-plus" onClick={() => setDelta(Math.abs(delta))} className="h-11 px-3 border border-velta-border rounded-md">+</button>
          </div>
          <input
            data-testid="loy-reason"
            placeholder="Motivo (es. Acquisto del 19/06, Premio bonus, ecc.)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            className="w-full h-11 px-3 border border-velta-border rounded-md text-sm"
          />
          <button
            data-testid="loy-adjust-submit"
            onClick={() => onAdjust(delta, reason)}
            className="w-full h-12 bg-[#2F9E77] text-white rounded-md font-semibold btn-tactile"
          >
            Applica {delta > 0 ? `+${delta}` : delta} punti
          </button>
        </div>
      </div>
    </div>
  );
}
